import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Download,
  FileCode,
  History,
  Eye
} from "lucide-react";

export default function Storico() {
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGen, setSelectedGen] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState("");

  useEffect(() => {
    const fetchGenerations = async () => {
      try {
        const res = await axios.get(`${API}/xml/generations`);
        setGenerations(res.data);
      } catch (e) {
        toast.error("Errore nel caricamento storico");
      } finally {
        setLoading(false);
      }
    };
    fetchGenerations();
  }, []);

  const handlePreview = async (gen) => {
    try {
      const res = await axios.get(`${API}/xml/generations/${gen.id}`);
      setPreviewContent(res.data.xml_content);
      setSelectedGen(gen);
      setShowPreview(true);
    } catch (e) {
      toast.error("Errore nel caricamento preview");
    }
  };

  const handleDownload = async (gen) => {
    try {
      const res = await axios.get(`${API}/xml/download/${gen.id}`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = gen.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Download avviato");
    } catch (e) {
      toast.error("Errore nel download");
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" data-testid="storico-title">
          Storico Generazioni
        </h1>
        <p className="text-slate-500 mt-1">
          Archivio dei file XML generati
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5" />
            File XML Generati ({generations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />
              ))}
            </div>
          ) : generations.length === 0 ? (
            <div className="text-center py-12">
              <FileCode className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessun file XML generato</p>
              <p className="text-sm text-slate-400 mt-1">
                Genera il tuo primo file dalla sezione "Genera XML"
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full data-table" data-testid="storico-table">
                <thead>
                  <tr>
                    <th>Nome File</th>
                    <th>Data Generazione</th>
                    <th>Data Incasso</th>
                    <th className="text-right">Transazioni</th>
                    <th className="text-right">Importo</th>
                    <th className="text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {generations.map((gen) => (
                    <tr key={gen.id} data-testid={`storico-row-${gen.id}`}>
                      <td className="font-mono text-xs">{gen.filename}</td>
                      <td>
                        {new Date(gen.created_at).toLocaleDateString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td>{gen.data_incasso}</td>
                      <td className="text-right tabular-nums">{gen.num_transactions}</td>
                      <td className="text-right tabular-nums font-medium">
                        € {gen.total_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreview(gen)}
                            data-testid={`preview-${gen.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(gen)}
                            data-testid={`download-${gen.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCode className="w-5 h-5" />
              {selectedGen?.filename}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-slate-500">Transazioni</p>
                <p className="font-semibold">{selectedGen?.num_transactions}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-slate-500">Importo</p>
                <p className="font-semibold">
                  € {selectedGen?.total_amount?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-slate-500">Data Incasso</p>
                <p className="font-semibold">{selectedGen?.data_incasso}</p>
              </div>
            </div>
            <div className="xml-preview max-h-[50vh] overflow-auto">
              <pre>{previewContent}</pre>
            </div>
            <div className="flex justify-end">
              <Button 
                onClick={() => handleDownload(selectedGen)} 
                className="gap-2"
                data-testid="download-preview-btn"
              >
                <Download className="w-4 h-4" />
                Scarica XML
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
